/* TrialGateBanner — Renders a dismissible banner for legacy users who
 * haven't added a card yet. Hard-redirects to /welcome?cancelled=true
 * (the retry screen) when the soft grace window has expired.
 *
 * Shipped Feb 2026 alongside the mandatory-card-on-signup change.
 * Existing users get a 7-day grace period; after that they're hard-gated
 * just like brand-new signups.
 *
 * Polls /api/billing/trial-status on mount and then every 5 minutes.
 * Skips entirely on public routes (landing, login, welcome, terms,
 * privacy, portal) so cancellation flows aren't broken.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../fetchInterceptor';
import { useAuth } from '../context/AuthContext';
import { isSetupGateActive } from '../lib/setupGate';
import { AlertTriangle, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Routes where the banner / hard-gate should NOT fire. Lets unauthenticated
// pages, the retry flow, billing settings, support, and the portal stay
// usable without the gate stepping on them.
const SKIP_PREFIXES = [
  '/login', '/welcome', '/auth-callback',
  '/terms', '/privacy', '/features', '/portal',
  '/sign/',  // public e-sign pages
  '/setup-wizard',  // first-run workspace setup must finish before the gate
];

const DISMISS_KEY = 'trial_gate_banner_dismissed_until';

export default function TrialGateBanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  // Lazy init — read localStorage once on mount. Avoids the
  // react-hooks/set-state-in-effect lint warning that fires when we set
  // state inside an empty-dep useEffect.
  const [dismissed, setDismissed] = useState(() => {
    try {
      const until = parseInt(window.localStorage.getItem(DISMISS_KEY) || '0', 10);
      return Boolean(until && Date.now() < until);
    } catch { return false; }
  });

  // Hide on public + billing routes. Also skip entirely when the current
  // session is an admin-view (Owner Admin working inside a customer
  // workspace) — the admin's shell `users` doc isn't a paying customer.
  const path = location.pathname;
  const skip = path === '/'
    || SKIP_PREFIXES.some((p) => path.startsWith(p))
    || Boolean(user?.admin_view)
    || Boolean(user?.impersonation);
  const onBillingPage = path.startsWith('/settings');

  // Poll the gate status. Re-runs on route change so users get the banner
  // immediately after their first navigation post-login.
  useEffect(() => {
    if (skip) return;
    if (!user) return; // Wait for AuthContext to hydrate before deciding.
    let alive = true;
    const tick = async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/billing/trial-status`);
        if (!r.ok) return;
        const d = await r.json();
        if (alive) setState(d);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, [skip, path, user]);

  // Hydrate dismissal flag from localStorage. Re-shows the banner after
  // 24h so the user keeps seeing the countdown to hard-gate.
  // (Initial value handled by lazy useState above.)

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch { /* ignore */ }
    setDismissed(true);
  };

  // Hard-gate: redirect to /welcome?cancelled=true so they get the
  // single-CTA retry screen. Skip if they're already on billing/welcome.
  //
  // Also honors a session-storage flag (`trial_gate_recently_cancelled`)
  // that gets set when the user bounces back from Stripe Checkout. Without
  // this, every page they navigate to fires another redirect — which is
  // exactly the "I didn't click anything and it took me to Stripe" UX
  // glitch caught in the Feb 2026 sign-up walk-through. The flag is
  // cleared after 15 minutes or once the user adds a card.
  useEffect(() => {
    if (!state || skip || onBillingPage) return;
    if (state.hard_gated && state.must_start_trial) {
      // Brand-new signup mid workspace-setup — defer the gate until the
      // setup wizard clears it (shared guard, see lib/setupGate.js).
      if (isSetupGateActive()) return;
      let recent = 0;
      try {
        recent = parseInt(window.sessionStorage.getItem('trial_gate_recently_cancelled') || '0', 10);
      } catch { /* ignore */ }
      if (recent && Date.now() - recent < 15 * 60 * 1000) {
        // Within the cool-off window — show the banner but DON'T force-nav.
        return;
      }
      navigate('/welcome?cancelled=true&plan=starter&cadence=monthly', { replace: true });
    }
  }, [state, skip, onBillingPage, navigate]);

  if (skip) return null;
  if (!state) return null;
  if (!state.must_start_trial) return null;
  if (state.hard_gated) return null;        // redirect already in flight
  if (!state.soft_warn) return null;
  if (dismissed) return null;

  return (
    <div
      data-testid="trial-gate-banner"
      className="relative z-50 bg-amber-500/15 border-b border-amber-500/30 text-amber-100 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0" />
        <p className="text-sm flex-1 leading-relaxed">
          <span className="font-semibold">Add your card to keep CountBucks active.</span>{' '}
          You have <span className="font-mono font-bold">{state.days_until_hard_gate}</span> day
          {state.days_until_hard_gate === 1 ? '' : 's'} before your account is locked.
          {' '}Free for 7 days — cancel anytime.
        </p>
        <button
          type="button"
          onClick={() => navigate('/settings?tab=billing&auto=1&plan_intent=starter&cadence=monthly')}
          data-testid="trial-gate-add-card-btn"
          className="text-xs font-semibold uppercase tracking-wider bg-amber-400 hover:bg-amber-300 text-amber-950 px-3 py-1.5 rounded-md transition-colors"
        >
          Add card
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="trial-gate-dismiss-btn"
          aria-label="Dismiss for 24 hours"
          className="text-amber-200/70 hover:text-amber-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
